import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import "./SoutheasternNewEntry.css";

function SoutheasternNewEntry() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    dateReceived: "",
    dateScheduled: "",
    priorities: "",
    taylorTechnologyNumber: "",
    clientReferenceNo: "",
    locations: "",
    siteDetails: "",
    quotationNo: "",
    value: "",
    jobNo: "",
    station: "",
    descriptionOfWorks: "",
    returnVisits: false,
    cherryPickerRequired: false,
    returnSiteDetails: "",
    returnAdditionalNotes: ""
  });
  
  const [fieldErrors, setFieldErrors] = useState({});

  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    
    // If returnVisits checkbox is unchecked, clear return details fields
    if (name === 'returnVisits' && !checked) {
      setForm({ 
        ...form, 
        [name]: false,
        returnSiteDetails: "",
        returnAdditionalNotes: "",
        cherryPickerRequired: false
      });
    } else {
      // For Taylor Technology Number, only allow numbers
      if (name === 'taylorTechnologyNumber' && type !== 'checkbox') {
        const numericValue = value.replace(/[^0-9]/g, '');
        setForm({ 
          ...form, 
          [name]: numericValue 
        });
      } 
      // For Job No, only allow numbers
      else if (name === 'jobNo' && type !== 'checkbox') {
        const numericValue = value.replace(/[^0-9]/g, '');
        setForm({ 
          ...form, 
          [name]: numericValue 
        });
      } else {
        setForm({ 
          ...form, 
          [name]: type === 'checkbox' ? checked : value 
        });
      }
    }
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous field errors
    setFieldErrors({});
    
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Authentication token not found. Please login again.");
        return;
      }
      
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      // Add TTL prefix to Taylor Technology Number if not already present
      const formData = { ...form };
      if (formData.taylorTechnologyNumber && !formData.taylorTechnologyNumber.toUpperCase().startsWith('TTL')) {
        formData.taylorTechnologyNumber = `TTL${formData.taylorTechnologyNumber}`;
      }
      
      await axios.post(`${baseUrl}/api/southeastern`, formData, config);
      toast.success("Southeastern entry submitted successfully");
      navigate("/admin/southeastern/entry-data");
    } catch (err) {
      console.error("Error:", err);
      const responseData = err?.response?.data;
      
      // Handle validation errors from backend
      if (responseData && responseData.field) {
        const { field, message } = responseData;
        setFieldErrors({ [field]: message });
        if (message) {
          toast.error(message);
        }
        
        // Focus on the problematic field instead of showing toast
        setTimeout(() => {
          const fieldElement = document.querySelector(`[name="${field}"]`);
          if (fieldElement) {
            fieldElement.focus();
            fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      } else if (responseData?.message) {
        toast.error(responseData.message);
      } else {
        toast.error("Failed to submit Southeastern entry");
      }
    }
  };

  return (
    <div className="southeastern-container">
      <div className="southeastern-form">
        <div className="form-header">
          <h1 className="form-title">Southeastern</h1>
          <h2 className="form-subtitle">New Entry</h2>
        </div>

        <form onSubmit={handleSubmit} className="southeastern-form-content">
          {/* Section 1 - Basic Information */}
          <div className="form-section">
            <h3 className="section-title">Basic Information</h3>
            <div className="form-row">
              <div className="form-field">
                <label className="field-label">Date Received *</label>
                <input
                  type="date"
                  name="dateReceived"
                  value={form.dateReceived}
                  onChange={handleChange}
                  className="form-input"
                  required
                />
              </div>
              
              <div className="form-field">
                <label className="field-label">Date Scheduled</label>
                <input
                  type="date"
                  name="dateScheduled"
                  value={form.dateScheduled}
                  onChange={handleChange}
                  className="form-input"
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              
              <div className="form-field">
                <label className="field-label">Priorities *</label>
                <select
                  name="priorities"
                  value={form.priorities}
                  onChange={handleChange}
                  className="form-input"
                  required
                >
                  <option value="">Select Priority</option>
                  <option value="Priority A (2 days)">Priority A (2 days)</option>
                  <option value="Priority B (3 days)">Priority B (3 days)</option>
                  <option value="Priority C (9 days)">Priority C (9 days)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Section 2 - Job Details */}
          <div className="form-section">
            <h3 className="section-title">Job Details</h3>
            <div className="form-row">
              <div className="form-field">
                <label className="field-label">Taylor Technology Number *</label>
                <input
                  type="text"
                  name="taylorTechnologyNumber"
                  value={form.taylorTechnologyNumber}
                  onChange={handleChange}
                  className={`form-input ${fieldErrors.taylorTechnologyNumber ? 'error' : ''}`}
                  placeholder="Enter Taylor Technology Number"
                  required
                />
                {fieldErrors.taylorTechnologyNumber && (
                  <div className="field-error">{fieldErrors.taylorTechnologyNumber}</div>
                )}
              </div>
              
              <div className="form-field">
                <label className="field-label">Client Reference No *</label>
                <input
                  type="text"
                  name="clientReferenceNo"
                  value={form.clientReferenceNo}
                  onChange={handleChange}
                  className={`form-input ${fieldErrors.clientReferenceNo ? 'error' : ''}`}
                  placeholder="Enter Client Reference Number"
                  required
                />
                {fieldErrors.clientReferenceNo && (
                  <div className="field-error">{fieldErrors.clientReferenceNo}</div>
                )}
              </div>
              
              <div className="form-field">
                <label className="field-label">Job No</label>
                <input
                  type="text"
                  name="jobNo"
                  value={form.jobNo}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Enter Job Number"
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-field">
                <label className="field-label">Quotation No</label>
                <input
                  type="text"
                  name="quotationNo"
                  value={form.quotationNo}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Enter Quotation Number"
                />
              </div>
              
              <div className="form-field">
                <label className="field-label">Value</label>
                <input
                  type="text"
                  name="value"
                  value={form.value}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Enter Value"
                />
              </div>
              
              <div className="form-field">
                <label className="field-label">Station</label>
                <input
                  type="text"
                  name="station"
                  value={form.station}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Enter Station"
                />
              </div>
            </div>
          </div>

          {/* Section 3 - Location & Site Details */}
          <div className="form-section">
            <h3 className="section-title">Location & Site Details</h3>
            <div className="form-row">
              <div className="form-field">
                <label className="field-label">Locations *</label>
                <input
                  type="text"
                  name="locations"
                  value={form.locations}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Enter Locations"
                  required
                />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-field full-width">
                <label className="field-label">Site Details *</label>
                <textarea
                  name="siteDetails"
                  value={form.siteDetails}
                  onChange={handleChange}
                  className="form-textarea"
                  placeholder="Enter Site Details"
                  rows="4"
                  required
                />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-field full-width">
                <label className="field-label">Description of Works</label>
                <textarea
                  name="descriptionOfWorks"
                  value={form.descriptionOfWorks}
                  onChange={handleChange}
                  className="form-textarea"
                  placeholder="Enter Description of Works"
                  rows="4"
                />
              </div>
            </div>
          </div>

          {/* Section 5 - Requirements */}
          <div className="form-section">
            <h3 className="section-title">Requirements</h3>
            <div className="checkbox-section">
              <div className="checkbox-field">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="returnVisits"
                    checked={form.returnVisits}
                    onChange={handleChange}
                    className="checkbox-input"
                  />
                  <span className="checkbox-text">RETURN VISITS</span>
                </label>
              </div>
            </div>
          </div>

          {/* Section 6 - Return Details (Conditional) */}
          {form.returnVisits && (
            <div className="form-section return-details-section">
              <h3 className="section-title">Return Details</h3>
              
              <div className="form-row">
                <div className="form-field full-width">
                  <label className="field-label">RETURN Site Details</label>
                  <textarea
                    name="returnSiteDetails"
                    value={form.returnSiteDetails}
                    onChange={handleChange}
                    className="form-textarea"
                    placeholder="Enter RETURN Site Details"
                    rows="3"
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-field full-width">
                  <label className="field-label">RETURN Additional Notes</label>
                  <textarea
                    name="returnAdditionalNotes"
                    value={form.returnAdditionalNotes}
                    onChange={handleChange}
                    className="form-textarea"
                    placeholder="Enter RETURN Additional Notes"
                    rows="3"
                  />
                </div>
              </div>
              
              {/* Cherry Picker Required Checkbox */}
              <div className="checkbox-section">
                <div className="checkbox-field">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="cherryPickerRequired"
                      checked={form.cherryPickerRequired}
                      onChange={handleChange}
                      className="checkbox-input"
                    />
                    <span className="checkbox-text">Cherry Picker Required</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="form-actions">
            <button type="submit" className="submit-btn">
              Submit
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default SoutheasternNewEntry;
